import { createContext, useContext, useMemo } from 'react';
import { Alert as ChakraAlert, AlertProps, forwardRef } from '@chakra-ui/react';

import { ColorScheme } from '../../types/bitkit';
import CloseButton from '../CloseButton/CloseButton';
import ColorButton, { ColorButtonProps } from '../ColorButton/ColorButton';
import Box from '../Box/Box';

export type ActionProps = {
  label: string;
  href?: string;
  onClick?: () => void;
} & Omit<ColorButtonProps, 'as' | 'onClick' | 'colorScheme'>;

export interface RibbonProps extends Omit<AlertProps, 'status' | 'colorScheme' | 'onClose'> {
  colorScheme: ColorScheme;
  onClose?: () => void;
  action?: ActionProps;
}

const ActionContext = createContext<{
  colorScheme: ColorButtonProps['colorScheme'];
}>({ colorScheme: 'blue' });

const RibbonAction = ({ href, label, ...rest }: ActionProps) => {
  const actionContext = useContext(ActionContext);
  return (
    <ColorButton
      as={(href ? 'a' : 'button') as any}
      flexGrow={0}
      flexShrink={0}
      href={href}
      marginY={-4}
      {...actionContext}
      {...rest}
    >
      {label}
    </ColorButton>
  );
};

const Ribbon = forwardRef<RibbonProps, 'div'>((props, ref) => {
  const { action, children, colorScheme, onClose, ...rest } = props;
  const actionContext = useMemo(() => ({ colorScheme }), [colorScheme]);
  let actionButton;
  if (action) {
    actionButton = <RibbonAction {...action} />;
  }

  return (
    <ChakraAlert
      ref={ref}
      alignItems="center"
      borderLeftWidth={0}
      borderRadius={0}
      borderRightWidth={0}
      colorScheme={colorScheme}
      gap={16}
      justifyContent="center"
      {...rest}
    >
      <ActionContext.Provider value={actionContext}>
        {onClose && <Box display={['none', 'block']} height={24} marginEnd="auto" width={24} />}
        <Box display="flex" flexDirection={['column', 'row']} gap={['12', '16']}>
          {children}
          {actionButton}
        </Box>
        {onClose && <CloseButton colorScheme={colorScheme} flexShrink={0} marginStart="auto" onClick={onClose} />}
      </ActionContext.Provider>
    </ChakraAlert>
  );
});

export default Ribbon;
